import { canonicalStringify, getCanonicalFnv1a64Hash } from "@fate-kingdom/shared";

import {
  LANE_IDS,
  type CardInstanceId,
  type CardType,
  type EngineCardDefinitionV2,
  type EngineCardDefinitionV3,
  type EngineCardDefinitionV4,
  type GameAction,
  type GamePhase,
  type CurrentCoreAlphaGameState,
  type LaneId,
  type LaneResult,
  type LaneSideStatus,
  type PlayerId,
  type ResourcePool,
  type TurnActionUsage,
} from "../types";
import { getTotalArmySize } from "../selectors/garrison";
import { getTotalAttack, getTotalGuard } from "../selectors/combat";

export const PLAYER_OBSERVATION_VERSION = 1 as const;
export const PLAYER_OBSERVATION_VERSION_V2 = 2 as const;
export const PLAYER_OBSERVATION_VERSION_V3 = 3 as const;
export const PLAYER_OBSERVATION_HASH_PREFIX = "player-observation-fnv1a64-v1";
export const PLAYER_OBSERVATION_V2_HASH_PREFIX = "player-observation-fnv1a64-v2";
export const PLAYER_OBSERVATION_V3_HASH_PREFIX = "player-observation-fnv1a64-v3";

export interface VisibleResourcePoolV1 {
  readonly food: number;
  readonly production: number;
  readonly trade: number;
  readonly science: number;
  readonly culture: number;
}

export interface VisibleLandStageV1 {
  readonly stage: 1 | 2 | 3;
  readonly maximumControl: number;
  readonly production: VisibleResourcePoolV1;
}

interface VisibleCardBaseV1 {
  readonly cardInstanceId: CardInstanceId;
  readonly cardDefinitionId: string;
  readonly name: string;
  readonly cost: VisibleResourcePoolV1 | null;
}

export interface VisibleLandCardViewV1 extends VisibleCardBaseV1 {
  readonly type: "land";
  readonly landTypeId: string;
  readonly developmentId: string;
  readonly stages: readonly [VisibleLandStageV1, VisibleLandStageV1, VisibleLandStageV1];
}

export interface VisibleCommanderCardViewV1 extends VisibleCardBaseV1 {
  readonly type: "commander";
  readonly attack: number | null;
  readonly guard: number | null;
  /** Historical V1/V2 field; intentionally absent from public-beta V3. */
  readonly armyCapacity?: number | null;
}

export interface VisibleArmyCardViewV1 extends VisibleCardBaseV1 {
  readonly type: "army";
  readonly armySize: number;
  readonly attack: number;
  readonly guard: number;
  readonly upkeep: VisibleResourcePoolV1;
}

export interface VisibleOtherCardViewV1 extends VisibleCardBaseV1 {
  readonly type: Exclude<CardType, "land" | "commander" | "army">;
}

export type VisibleCardViewV1 =
  | VisibleLandCardViewV1
  | VisibleCommanderCardViewV1
  | VisibleArmyCardViewV1
  | VisibleOtherCardViewV1;

export interface VisibleBattlefieldLandV1 {
  readonly cards: readonly VisibleLandCardViewV1[];
  readonly stage: 1 | 2 | 3;
  readonly currentControl: number;
  readonly maximumControl: number;
  readonly attackBonus: number;
  readonly guardBonus: number;
}

export interface VisibleBattlefieldCommanderV1 extends VisibleCommanderCardViewV1 {
  readonly readiness: "ready" | "spent";
  readonly deployedThisTurn: boolean;
  readonly mayAttackOnDeploymentTurn: boolean;
}

export type VisibleBattlefieldArmyV1 = VisibleArmyCardViewV1;

export interface OwnVisibleLaneV1 {
  readonly status: LaneSideStatus;
  readonly land: VisibleBattlefieldLandV1 | null;
  readonly commander: VisibleBattlefieldCommanderV1 | null;
  readonly armies: readonly VisibleBattlefieldArmyV1[];
  readonly conspiracy: VisibleOtherCardViewV1 | null;
  readonly formation?: VisibleFormationV2;
}

export interface OpponentVisibleLaneV1 {
  readonly status: LaneSideStatus;
  readonly land: VisibleBattlefieldLandV1 | null;
  readonly commander: VisibleBattlefieldCommanderV1 | null;
  readonly armies: readonly VisibleBattlefieldArmyV1[];
  /** A face-down Conspiracy's existence is public; its identity is not. */
  readonly hasFaceDownCard: boolean;
  readonly formation?: VisibleFormationV2;
}

/** Public, precomputed formation facts; never a client-supplied combat result. */
export interface VisibleFormationV2 {
  readonly totalAttack: number;
  readonly totalGuard: number;
  readonly armyCount: number;
  /** Kept alongside totalArmySize for concise presentation labels. */
  readonly armySize: number;
  readonly totalArmySize: number;
  /** Historical V1/V2 field; intentionally absent from public-beta V3. */
  readonly armyCapacity?: number | null;
  readonly commanderReadiness: "ready" | "spent" | null;
  readonly preparing: boolean;
  readonly attackedThisTurn: boolean;
}

export interface VisibleKingdomProgressV2 {
  readonly populatedLaneIds: readonly LaneId[];
  readonly complete: boolean;
}

export interface VisibleNoTerritoryWarningV2 {
  readonly threatenedPlayerId: PlayerId;
  readonly threateningPlayerId: PlayerId;
  readonly deadlineTurnNumber: number | null;
}

export interface VisibleLaneV1 {
  readonly laneId: LaneId;
  readonly result: LaneResult;
  readonly own: OwnVisibleLaneV1;
  readonly opponent: OpponentVisibleLaneV1;
}

export interface VisibleTurnActionUsageV1 {
  readonly landActions: number;
  readonly deploymentActions: number;
  readonly governanceActions: number;
  readonly eventsPlayed: number;
  readonly eventAuthorizationLanesUsed: readonly LaneId[];
  readonly attacksMadeFromLanes: readonly LaneId[];
}

interface VisiblePublicPlayerV1 {
  readonly playerId: PlayerId;
  readonly handCount: number;
  readonly deckCount: number;
  readonly discard: readonly VisibleCardViewV1[];
  readonly exhaust: readonly VisibleCardViewV1[];
  readonly eventResolution: readonly VisibleCardViewV1[];
  readonly activePolicy: VisibleOtherCardViewV1 | null;
  readonly kingdomPool: VisibleResourcePoolV1;
  readonly actionsUsed: VisibleTurnActionUsageV1;
  readonly victoryBanners: number;
}

export interface PerspectivePlayerObservationV1 extends VisiblePublicPlayerV1 {
  /** Full identity and printed information are visible in the owner's hand. */
  readonly hand: readonly VisibleCardViewV1[];
}

export type OpponentPlayerObservationV1 = VisiblePublicPlayerV1;

/**
 * Version 1 player-safe match projection. It intentionally excludes engine
 * random state, deck arrays, action/event audits, complete card registry, and
 * the opponent's hand or face-down Conspiracy identity.
 */
export interface PlayerObservationV1 {
  readonly observationVersion:
    | typeof PLAYER_OBSERVATION_VERSION
    | typeof PLAYER_OBSERVATION_VERSION_V2
    | typeof PLAYER_OBSERVATION_VERSION_V3;
  readonly matchId: string;
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly turnNumber: number;
  readonly phase: GamePhase;
  readonly activePlayerId: PlayerId;
  readonly status:
    | { readonly type: "active" }
    | {
        readonly type: "finished";
        readonly winnerPlayerId: PlayerId;
        readonly reason: "two-victory-banners" | "no-territory";
      };
  readonly perspectivePlayerId: PlayerId;
  readonly opponentPlayerId: PlayerId;
  readonly own: PerspectivePlayerObservationV1;
  readonly opponent: OpponentPlayerObservationV1;
  readonly lanes: Readonly<Record<LaneId, VisibleLaneV1>>;
  /** Present only for schema-V4 Flank/Kingdom Complete matches. */
  readonly kingdomProgress?: Readonly<{
    own: VisibleKingdomProgressV2;
    opponent: VisibleKingdomProgressV2;
  }>;
  readonly noTerritoryWarning?: VisibleNoTerritoryWarningV2 | null;
  /** Public-beta player-facing turn state; legacy observations omit it. */
  readonly turnState?: "mandatory-supply" | "action-window" | "finished";
}

export interface PlayerDecisionContext {
  readonly step: number;
  readonly playerId: PlayerId;
  readonly observation: PlayerObservationV1;
  readonly observationHash: string;
  readonly legalActions: readonly GameAction[];
}

export interface PlayerActionSource {
  readonly playerId: PlayerId;
  nextAction(context: Readonly<PlayerDecisionContext>): GameAction | null;
}

/** A structured source failure that does not require runner-to-bot coupling. */
export class PlayerActionSourceFailure extends Error {
  readonly code: "EMPTY_LEGAL_ACTION_SET" | "WRONG_PLAYER_CONTEXT";
  readonly details: Readonly<Record<string, string | number | boolean | null>>;

  constructor(
    code: "EMPTY_LEGAL_ACTION_SET" | "WRONG_PLAYER_CONTEXT",
    message: string,
    details: Readonly<Record<string, string | number | boolean | null>>,
  ) {
    super(message);
    this.name = "PlayerActionSourceFailure";
    this.code = code;
    this.details = details;
  }
}

function copyPool(pool: Readonly<ResourcePool>): VisibleResourcePoolV1 {
  return {
    food: pool.food,
    production: pool.production,
    trade: pool.trade,
    science: pool.science,
    culture: pool.culture,
  };
}

function copyActionsUsed(usage: Readonly<TurnActionUsage>): VisibleTurnActionUsageV1 {
  return {
    landActions: usage.landActions,
    deploymentActions: usage.deploymentActions,
    governanceActions: usage.governanceActions,
    eventsPlayed: usage.eventsPlayed,
    eventAuthorizationLanesUsed: [...usage.eventAuthorizationLanesUsed],
    attacksMadeFromLanes: [...usage.attacksMadeFromLanes],
  };
}

function isResourcePool(value: unknown): value is ResourcePool {
  if (value === null || typeof value !== "object") return false;
  const pool = value as Partial<ResourcePool>;
  return (
    typeof pool.food === "number" &&
    typeof pool.production === "number" &&
    typeof pool.trade === "number" &&
    typeof pool.science === "number" &&
    typeof pool.culture === "number"
  );
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function visibleCardForInstance(
  state: Readonly<CurrentCoreAlphaGameState>,
  cardInstanceId: CardInstanceId,
): VisibleCardViewV1 {
  const instance = state.cardInstances[cardInstanceId];
  const definition = state.catalog.cards.find(
    (
      candidate,
    ): candidate is EngineCardDefinitionV2 | EngineCardDefinitionV3 | EngineCardDefinitionV4 =>
      candidate.id === instance?.cardDefinitionId,
  );
  if (definition === undefined) {
    throw new Error(`Cannot project unknown card instance '${cardInstanceId}'.`);
  }

  const base = {
    cardInstanceId,
    cardDefinitionId: definition.id,
    name: definition.name,
    cost:
      "cost" in definition && isResourcePool(definition.cost) ? copyPool(definition.cost) : null,
  } as const;

  if (definition.type === "land") {
    const stages = definition.stages.map((stage) => ({
      stage: stage.stage,
      maximumControl: stage.maximumControl,
      production: copyPool(stage.production),
    })) as unknown as [VisibleLandStageV1, VisibleLandStageV1, VisibleLandStageV1];
    return {
      ...base,
      type: "land",
      landTypeId: definition.landTypeId,
      developmentId: definition.developmentId,
      stages,
    };
  }

  if (definition.type === "army") {
    return {
      ...base,
      type: "army",
      armySize: definition.armySize,
      attack: definition.attack,
      guard: definition.guard,
      upkeep: copyPool(definition.upkeep),
    };
  }

  if (definition.type === "commander") {
    return {
      ...base,
      type: "commander",
      attack: "attack" in definition ? readNumber(definition.attack) : null,
      guard: "guard" in definition ? readNumber(definition.guard) : null,
      ...("armyCapacity" in definition
        ? { armyCapacity: readNumber(definition.armyCapacity) }
        : {}),
    };
  }

  return { ...base, type: definition.type };
}

function visibleLandForSide(
  state: Readonly<CurrentCoreAlphaGameState>,
  land: Readonly<CurrentCoreAlphaGameState["lanes"][LaneId]["players"][PlayerId]["land"]>,
): VisibleBattlefieldLandV1 | null {
  if (land === null) return null;
  const cards = land.cardInstanceIds.map((cardInstanceId) => {
    const card = visibleCardForInstance(state, cardInstanceId);
    if (card.type !== "land") {
      throw new Error(`Land permanent '${cardInstanceId}' does not reference a Land card.`);
    }
    return card;
  });
  return {
    cards,
    stage: land.stage,
    currentControl: land.currentControl,
    maximumControl: land.maximumControl,
    attackBonus: land.attackBonus,
    guardBonus: land.guardBonus,
  };
}

function visibleCommanderForSide(
  state: Readonly<CurrentCoreAlphaGameState>,
  commander: Readonly<CurrentCoreAlphaGameState["lanes"][LaneId]["players"][PlayerId]["commander"]>,
): VisibleBattlefieldCommanderV1 | null {
  if (commander === null) return null;
  const card = visibleCardForInstance(state, commander.cardInstanceId);
  if (card.type !== "commander") {
    throw new Error(
      `Commander permanent '${commander.cardInstanceId}' does not reference a Commander.`,
    );
  }
  return {
    ...card,
    attack: commander.attack,
    guard: commander.guard,
    ...(state.schemaVersion === 5 ? {} : { armyCapacity: commander.armyCapacity ?? null }),
    readiness: commander.readiness,
    deployedThisTurn: commander.deployedThisTurn,
    mayAttackOnDeploymentTurn: commander.mayAttackOnDeploymentTurn,
  };
}

function visibleArmiesForSide(
  state: Readonly<CurrentCoreAlphaGameState>,
  armies: Readonly<CurrentCoreAlphaGameState["lanes"][LaneId]["players"][PlayerId]["armies"]>,
): readonly VisibleBattlefieldArmyV1[] {
  return armies.map((army) => {
    const card = visibleCardForInstance(state, army.cardInstanceId);
    if (card.type !== "army") {
      throw new Error(`Army permanent '${army.cardInstanceId}' does not reference an Army card.`);
    }
    return {
      ...card,
      armySize: army.armySize,
      attack: army.attack,
      guard: army.guard,
    };
  });
}

function visibleOtherCardOrNull(
  state: Readonly<CurrentCoreAlphaGameState>,
  cardInstanceId: CardInstanceId | null,
): VisibleOtherCardViewV1 | null {
  if (cardInstanceId === null) return null;
  const card = visibleCardForInstance(state, cardInstanceId);
  if (card.type === "land" || card.type === "commander" || card.type === "army") {
    throw new Error(`Deferred permanent '${cardInstanceId}' does not reference a deferred card.`);
  }
  return card;
}

function visibleFormation(
  state: Readonly<CurrentCoreAlphaGameState>,
  playerId: PlayerId,
  laneId: LaneId,
): VisibleFormationV2 {
  const side = state.lanes[laneId].players[playerId];
  const commander = side.commander;
  return {
    totalAttack: getTotalAttack(state, playerId, laneId),
    totalGuard: getTotalGuard(state, playerId, laneId),
    armyCount: side.armies.length,
    armySize: getTotalArmySize(state, playerId, laneId),
    totalArmySize: getTotalArmySize(state, playerId, laneId),
    ...(state.schemaVersion === 5 ? {} : { armyCapacity: commander?.armyCapacity ?? null }),
    commanderReadiness: commander?.readiness ?? null,
    preparing:
      commander !== null && commander.deployedThisTurn && !commander.mayAttackOnDeploymentTurn,
    attackedThisTurn: state.players[playerId].actionsUsed.attacksMadeFromLanes.includes(laneId),
  };
}

function buildPublicPlayer(
  state: Readonly<CurrentCoreAlphaGameState>,
  playerId: PlayerId,
): VisiblePublicPlayerV1 {
  const player = state.players[playerId];
  return {
    playerId,
    handCount: player.hand.length,
    deckCount: player.deck.length,
    discard: player.discard.map((cardInstanceId) => visibleCardForInstance(state, cardInstanceId)),
    exhaust: player.exhaust.map((cardInstanceId) => visibleCardForInstance(state, cardInstanceId)),
    eventResolution: player.eventResolution.map((cardInstanceId) =>
      visibleCardForInstance(state, cardInstanceId),
    ),
    activePolicy: visibleOtherCardOrNull(state, player.activePolicyId),
    kingdomPool: copyPool(player.kingdomPool),
    actionsUsed: copyActionsUsed(player.actionsUsed),
    victoryBanners: player.victoryBanners,
  };
}

function getOpponentPlayerId(playerId: PlayerId): PlayerId {
  return playerId === "player-1" ? "player-2" : "player-1";
}

/**
 * Projects exactly one player's legal view from a current schema-V3/V4 state. The returned
 * graph is newly allocated and contains no references to engine-owned state.
 */
export function createPlayerObservation(
  state: Readonly<CurrentCoreAlphaGameState>,
  perspectivePlayerId: PlayerId,
): PlayerObservationV1 {
  const opponentPlayerId = getOpponentPlayerId(perspectivePlayerId);
  const ownPlayer = state.players[perspectivePlayerId];
  const lanes = {} as Record<LaneId, VisibleLaneV1>;

  for (const laneId of LANE_IDS) {
    const lane = state.lanes[laneId];
    const own = lane.players[perspectivePlayerId];
    const opponent = lane.players[opponentPlayerId];
    lanes[laneId] = {
      laneId,
      result: lane.result,
      own: {
        status: own.status,
        land: visibleLandForSide(state, own.land),
        commander: visibleCommanderForSide(state, own.commander),
        armies: visibleArmiesForSide(state, own.armies),
        conspiracy: visibleOtherCardOrNull(state, own.conspiracyCardInstanceId),
        formation: visibleFormation(state, perspectivePlayerId, laneId),
      },
      opponent: {
        status: opponent.status,
        land: visibleLandForSide(state, opponent.land),
        commander: visibleCommanderForSide(state, opponent.commander),
        armies: visibleArmiesForSide(state, opponent.armies),
        hasFaceDownCard: opponent.conspiracyCardInstanceId !== null,
        formation: visibleFormation(state, opponentPlayerId, laneId),
      },
    };
  }

  const status: PlayerObservationV1["status"] =
    state.status.type === "active"
      ? { type: "active" }
      : {
          type: "finished",
          winnerPlayerId: state.status.winnerPlayerId,
          reason: state.status.reason,
        };
  const base = {
    observationVersion:
      state.schemaVersion === 5
        ? PLAYER_OBSERVATION_VERSION_V3
        : state.schemaVersion === 4
          ? PLAYER_OBSERVATION_VERSION_V2
          : PLAYER_OBSERVATION_VERSION,
    matchId: state.matchId,
    rulesVersion: state.rulesVersion,
    cardDataVersion: state.cardDataVersion,
    gameplayCatalogHash: state.catalog.gameplayCatalogHash,
    turnNumber: state.turnNumber,
    phase: state.phase,
    activePlayerId: state.activePlayerId,
    status,
    perspectivePlayerId,
    opponentPlayerId,
    own: {
      ...buildPublicPlayer(state, perspectivePlayerId),
      hand: ownPlayer.hand.map((cardInstanceId) => visibleCardForInstance(state, cardInstanceId)),
    },
    opponent: buildPublicPlayer(state, opponentPlayerId),
    lanes,
  } as const;
  return state.schemaVersion === 4 || state.schemaVersion === 5
    ? {
        ...base,
        kingdomProgress: {
          own: { ...state.kingdomProgress[perspectivePlayerId] },
          opponent: { ...state.kingdomProgress[opponentPlayerId] },
        },
        noTerritoryWarning:
          state.noTerritoryWarning === null ? null : { ...state.noTerritoryWarning },
        ...(state.schemaVersion === 5
          ? {
              turnState:
                state.status.type === "finished"
                  ? ("finished" as const)
                  : state.supplyStatus.type === "awaiting-upkeep-discard"
                    ? ("mandatory-supply" as const)
                    : ("action-window" as const),
            }
          : {}),
      }
    : base;
}

export function getPlayerObservationCanonicalString(
  observation: Readonly<PlayerObservationV1>,
): string {
  return canonicalStringify(observation);
}

export function getPlayerObservationHash(observation: Readonly<PlayerObservationV1>): string {
  return getCanonicalFnv1a64Hash(
    observation,
    observation.observationVersion === PLAYER_OBSERVATION_VERSION_V3
      ? PLAYER_OBSERVATION_V3_HASH_PREFIX
      : observation.observationVersion === PLAYER_OBSERVATION_VERSION_V2
        ? PLAYER_OBSERVATION_V2_HASH_PREFIX
        : PLAYER_OBSERVATION_HASH_PREFIX,
  );
}
